/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from '@radix-ui/react-slot'

import { cn } from 'app/lib/utils'
import Spinner from '../Spinner'

const buttonVariants = cva(
  `cursor-pointer group/button inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-transparent bg-clip-padding whitespace-nowrap transition-all outline-none select-none active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4`,
  {
    variants: {
      variant: {
        primary: `bg-primary-600 text-on-color hover:bg-primary-700 aria-expanded:bg-primary-700 disabled:border-0 disabled:bg-surface-strong disabled:text-minimal`,
        secondary: `border-primary-600 bg-surface text-primary-600 hover:bg-primary-alpha-8 aria-expanded:bg-primary-alpha-8 disabled:border-0 disabled:bg-surface-strong disabled:text-minimal`,
        white: `border-default bg-surface text-default hover:border-strong hover:bg-surface-muted aria-expanded:bg-surface-muted disabled:border-0 disabled:bg-surface-strong disabled:text-minimal`,
        tertiary: `border-default bg-surface text-default hover:border-strong hover:bg-surface-muted aria-expanded:bg-surface-muted disabled:border-0 disabled:bg-surface-strong disabled:text-minimal`,
        danger: `border-red-600 bg-surface text-red-600 hover:bg-red-50 aria-expanded:bg-red-50 disabled:border-0 disabled:bg-surface-strong disabled:text-minimal`,
        icon: `border-transparent bg-transparent p-0 aria-expanded:bg-transparent disabled:opacity-50`,
        // The filled icon affordances that sit inside the SMS composer box:
        // template, emoji, schedule. They need a resting fill, unlike `icon`,
        // so they read as controls against the composer's own surface.
        iconFilled: `border-transparent bg-surface-muted text-default hover:bg-surface-strong hover:text-strong aria-expanded:bg-surface-strong disabled:opacity-50`,
        // A filter chip: an aria-pressed toggle rather than an action. The
        // pressed look keys off the attribute, so the state and the styling
        // cannot disagree.
        toggle: `border-default bg-surface text-subtle hover:border-strong hover:text-strong aria-pressed:border-primary-600 aria-pressed:bg-primary-alpha-8 aria-pressed:text-primary-700 disabled:cursor-default disabled:opacity-100`,
        link: `border-transparent bg-transparent text-primary-600 hover:underline aria-expanded:bg-transparent disabled:opacity-50`,
        // A dark, near-black action on a light surface: the one composer send button that stays
        // legible over the standard primary blue behind it.
        inverse: `bg-inverse text-inverse hover:bg-inverse/90 aria-expanded:bg-inverse/90 disabled:opacity-50`,
        // One option inside a segmented control (Off / Everyone / Specific): flat until pressed,
        // and the pressed look keys off aria-pressed like `toggle`.
        segment: `border-transparent bg-transparent text-subtle hover:text-default aria-pressed:bg-surface-muted aria-pressed:font-semibold aria-pressed:text-strong disabled:opacity-50`,
        // A row or header that acts like a button but reads as content at rest: list rows, disclosure
        // headers, click-to-edit titles. Transparent until hovered.
        ghost: `border-transparent bg-transparent text-default hover:bg-(--overlay-hover) focus-visible:bg-(--overlay-hover) aria-expanded:bg-transparent disabled:opacity-50`,
        // A whole card as one click target: quiet at rest, lifts on hover.
        card: `w-full justify-start whitespace-normal rounded-xl border-default bg-surface text-left text-strong shadow-sm hover:border-strong hover:bg-surface-subtle hover:shadow aria-expanded:bg-surface-subtle disabled:opacity-60`,
        // The muted action: close, back, carets, Remove, Cancel. The hover tint is translucent so it
        // darkens whatever surface the button sits on.
        subtle: `border-transparent bg-transparent text-subtle hover:bg-(--overlay-hover) focus-visible:bg-(--overlay-hover) hover:text-strong aria-expanded:bg-(--overlay-hover) disabled:opacity-50`,
        dashed: `border-dashed border-strong bg-transparent text-subtle hover:border-(--fg-minimal) hover:bg-surface-subtle disabled:opacity-50`,
        // Stands where an input would (date and picker triggers), so it wears inputVariants' look.
        field: `justify-start rounded-lg border-default bg-surface text-strong shadow-elevation-1 hover:border-strong focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 aria-invalid:border-red-400 disabled:bg-surface-muted disabled:text-subtle`,
      },
      size: {
        xs: `h-7 px-3 text-xs font-medium`,
        sm: `h-8 px-4 text-xs font-medium`,
        md: `h-9 px-4 text-sm font-medium`,
        lg: `h-12 px-6 py-4 text-heading-xs font-medium`,
        xl: `h-11 px-6 text-base font-medium`,
        icon: `size-9 font-medium`,
        'icon-sm': `size-7 font-medium`,
        row: `h-auto w-full justify-start gap-2 px-2 py-1.5 text-left font-normal`,
        // No font classes: preflight gives button `font: inherit`, so an inline action takes the
        // surrounding text's size, weight and line height.
        inline: `h-auto gap-1 p-0`,
      },
      shape: {
        default: ``,
        pill: `rounded-full`,
      },
    },
    // Compound classes merge after size classes, which is what lets these override a size's
    // padding, height and type.
    compoundVariants: [
      { variant: `card`, class: `h-auto gap-3 p-3` },
      { variant: `field`, class: `px-3 text-body-sm font-normal` },
      { variant: `subtle`, size: `inline`, class: `hover:bg-transparent aria-expanded:bg-transparent` },
      { variant: `ghost`, size: `inline`, class: `hover:bg-transparent aria-expanded:bg-transparent` },
      { variant: `link`, size: `inline`, class: `underline underline-offset-2` },
    ],
    defaultVariants: {
      variant: `primary`,
      size: `md`,
      shape: `default`,
    },
  },
)

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
  }

function Button({
  className,
  variant = `primary`,
  size = `md`,
  shape,
  asChild = false,
  loading = false,
  disabled,
  children,
  ref,
  ...props
}: ButtonProps) {
  const sharedProps = {
    'data-slot': 'button' as const,
    'data-variant': variant,
    'data-size': size,
    className: cn(buttonVariants({ variant, size, shape, className })),
  }
  const content = loading ? <Spinner /> : children

  if (asChild) {
    return (
      <Slot ref={ref} {...sharedProps} {...(props as React.HTMLAttributes<HTMLElement>)}>
        {content}
      </Slot>
    )
  }

  return (
    <button ref={ref} {...sharedProps} disabled={disabled || loading} {...props}>
      {content}
    </button>
  )
}

export { Button, buttonVariants }
